import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { updateMagazineCatch } from '@/api/magazineCatchApi';
import type { MagazineListItem } from '@/types/api.zod';
import { parseApiError } from '@/types/error';
import { buildCoverImageUrl } from '@/utils/coverImage';
import { logger } from '@/utils/logger';

/**
 * マガジンキャッチ編集モーダル (#233)
 *
 * - 編集対象は `magazine_catch` の 1 項目のみ (0〜50 文字)。
 *   `magazine_title` / `cover_image` は read-only 表示。
 * - 「更新」押下で `POST /admin/magazines/{title_name}/catch` を呼び、成功時に
 *   `onUpdated()` で一覧側に再取得を促してから `onClose()` でモーダルを閉じる。
 * - エラーは HTTP status で振り分け (400 / 401 / 403 / 404 / 500 / その他)。
 * - a11y: `role="dialog"` + `aria-modal="true"` + `aria-labelledby`。
 *   open 時に `<textarea>` へ focus、`Esc` でクローズ。
 */

const MAGAZINE_CATCH_MAX_LENGTH = 50;

interface MagazineCatchEditModalProps {
  /** 編集対象。`null` の場合はモーダルを描画しない。 */
  magazine: MagazineListItem | null;
  /** モーダルを閉じる。 */
  onClose: () => void;
  /** 更新成功後に呼ばれる (一覧の refetch トリガー)。 */
  onUpdated: () => void;
}

const buildErrorMessage = (error: unknown): string => {
  // Axios 以外 (Zod parse 失敗 / network 例外) のフォールバック
  if (
    !error ||
    typeof error !== 'object' ||
    !('response' in error) ||
    !error.response ||
    typeof error.response !== 'object' ||
    !('status' in error.response)
  ) {
    return 'サーバーエラーが発生しました。しばらくしてから再度お試しください。';
  }
  // 直前のガードで `error.response` がオブジェクトかつ `status` を持つことは確認済み。
  // `as { status: unknown }` を使わずに読む（AGENTS.md 1）。
  const status = Reflect.get(error.response, 'status');
  switch (status) {
    case 400: {
      const apiError = parseApiError(error);
      const detail = apiError?.detail || apiError?.error;
      const suffix = detail ? ` (${detail})` : '';
      return `入力内容に誤りがあります${suffix}`;
    }
    case 401:
    case 403:
      return '認証が無効です。再ログインしてください。';
    case 404:
      return '対象の雑誌が見つかりません。一覧を再読込してください。';
    default:
      return 'サーバーエラーが発生しました。しばらくしてから再度お試しください。';
  }
};

const MagazineCatchEditModal = ({ magazine, onClose, onUpdated }: MagazineCatchEditModalProps) => {
  const titleId = useId();
  const textareaId = useId();
  const counterId = useId();
  const errorId = useId();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const [catchValue, setCatchValue] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [imageFailed, setImageFailed] = useState<boolean>(false);

  // magazine が変わるたび state を初期化 (`useEffect` deps 完全)
  useEffect(() => {
    if (magazine) {
      setCatchValue(magazine.magazine_catch);
      setErrorMessage(null);
      setImageFailed(false);
    }
  }, [magazine]);

  // open 時に textarea に focus
  useEffect(() => {
    if (magazine) {
      textareaRef.current?.focus();
    }
  }, [magazine]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose();
  }, [onClose, submitting]);

  // Esc キーで close
  useEffect(() => {
    if (!magazine) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [magazine, handleClose]);

  if (!magazine) {
    return null;
  }

  const charCount = catchValue.length;
  const isOverLimit = charCount > MAGAZINE_CATCH_MAX_LENGTH;
  const isSubmitDisabled = submitting || isOverLimit;
  const coverImageUrl = buildCoverImageUrl(magazine.cover_image);
  const showImage = !!coverImageUrl && !imageFailed;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitDisabled) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await updateMagazineCatch(magazine.title_name, { magazine_catch: catchValue });
      onUpdated();
      onClose();
    } catch (err) {
      logger.logError('Failed to update magazine_catch', err);
      setErrorMessage(buildErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={(e) => void handleSubmit(e)}>
          <header className="px-6 py-4 border-b border-gray-200">
            <h2 id={titleId} className="text-lg font-bold text-admin-text">
              マガジンキャッチ編集
            </h2>
          </header>

          <div className="px-6 py-4 space-y-4">
            {/* read-only: 雑誌情報 */}
            <div className="flex items-start gap-4">
              <div
                className="shrink-0 w-16 h-24 bg-gray-100 border border-gray-200 rounded overflow-hidden flex items-center justify-center"
                aria-hidden={!showImage ? 'true' : undefined}
              >
                {showImage ? (
                  <img
                    src={coverImageUrl}
                    alt={`${magazine.magazine_title} の表紙`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={() => setImageFailed(true)}
                  />
                ) : (
                  <span className="text-xs text-admin-textLight">No Image</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-admin-textLight">雑誌名</p>
                <p className="text-base font-semibold text-admin-text break-words">
                  {magazine.magazine_title}
                </p>
                {magazine.magazine_series_title && (
                  <>
                    <p className="text-xs text-admin-textLight mt-2">シリーズ名</p>
                    <p className="text-sm text-admin-text break-words">
                      {magazine.magazine_series_title}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* edit: magazine_catch */}
            <div>
              <label
                htmlFor={textareaId}
                className="block text-sm font-medium text-admin-text mb-1"
              >
                マガジンキャッチ
              </label>
              <textarea
                id={textareaId}
                ref={textareaRef}
                value={catchValue}
                onChange={(e) => setCatchValue(e.target.value)}
                disabled={submitting}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-admin-textLight"
                aria-describedby={`${counterId}${errorMessage || isOverLimit ? ` ${errorId}` : ''}`}
                aria-invalid={isOverLimit ? 'true' : undefined}
              />
              <div className="flex items-center justify-between mt-1">
                <p
                  id={counterId}
                  className={`text-xs ${isOverLimit ? 'text-red-600 font-semibold' : 'text-admin-textLight'}`}
                >
                  {charCount}/{MAGAZINE_CATCH_MAX_LENGTH}
                </p>
              </div>
              {(isOverLimit || errorMessage) && (
                <p
                  id={errorId}
                  role="alert"
                  className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2"
                >
                  {isOverLimit
                    ? `${MAGAZINE_CATCH_MAX_LENGTH} 文字以内で入力してください。`
                    : errorMessage}
                </p>
              )}
            </div>
          </div>

          <footer className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 text-sm border border-gray-300 text-admin-text rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {submitting && (
                <span
                  className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"
                  aria-hidden="true"
                />
              )}
              {submitting ? '更新中...' : '更新'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default MagazineCatchEditModal;
